// pages/letters_create.jsx
import React, { useEffect, useContext, useMemo, useState } from "react";
import { Link } from "wouter";
import { AuthContext } from "../contexts/auth";
import Footer from "../components/footer";
import PopoverComponentCustom from "../components/popover_component";
import { MagnifyingGlassIcon, ExclamationTriangleIcon, Squares2X2Icon, Bars3Icon, FolderIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { getLettereList, getLettereCategories } from "../services/api_lettere";
import CheckPermissions from "../components/check_permissions";
import Swal from "sweetalert2";
import LoadingState from "../components/fetchingV2";

const ALL_KEY = "__ALL__";


function SidebarCategories({ categories, activeKey, onChange }) {
  return (
    <aside className="rounded-2xl bg-gradient-to-b from-indigo-50/60 to-white p-3 ring-1 ring-black/5">
      <nav className="space-y-2">
        <button
          type="button"
          onClick={() => onChange(ALL_KEY)}
          aria-current={activeKey === ALL_KEY ? "page" : undefined}
          className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 transition shadow-sm ${
            activeKey === ALL_KEY
              ? "bg-indigo-50 border border-indigo-500 text-indigo-500 uppercase"
              : "bg-white/70 hover:bg-indigo-50 text-gray-700 ring-1 ring-gray-200"
          }`}
        >
          <FolderIcon className="h-5 w-5 text-indigo-500 shrink-0" />
          <span className="block w-full whitespace-normal break-words text-left leading-snug text-sm font-medium">
            Tutte le lettere
          </span>
        </button>

        {categories.map((cat) => {
          const active = activeKey === cat.id;

          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => onChange(cat.id)}
              className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 transition shadow-sm ${
                active
                  ? "bg-indigo-50 border border-indigo-500 text-indigo-500 uppercase"
                  : "bg-white hover:bg-indigo-50 text-gray-700 ring-1 ring-gray-200"
              }`}
            >
              <FolderIcon className="h-5 w-5 block text-indigo-500 shrink-0" />
              <span className="block w-full whitespace-normal break-words text-left leading-snug text-sm font-medium">
                {cat.nome}
              </span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function SearchBar({ value, onChange }) {
  return (
    <div className="relative w-full">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Cerca fra tutte le lettere…"
        className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-3 py-2.5 text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
      />
    </div>
  );
}

function ViewToggle({ mode, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
      <button
        type="button"
        onClick={() => onChange("cards")}
        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm ${
          mode === "cards"
            ? "bg-indigo-600 text-white"
            : "text-gray-700 hover:bg-gray-50"
        }`}
      >
        <Squares2X2Icon className="h-4 w-4" />
        Schede
      </button>

      <button
        type="button"
        onClick={() => onChange("list")}
        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm ${
          mode === "list"
            ? "bg-indigo-600 text-white"
            : "text-gray-700 hover:bg-gray-50"
        }`}
      >
        <Bars3Icon className="h-4 w-4" />
        Lista
      </button>
    </div>
  );
}

function LetterRow({ letter, canGenerate, onLetterClick }) {
  return (
    <Link
      to={`/lettere/${letter.id}`}
      onClick={(event) => onLetterClick(event, canGenerate)}
      className="group relative block px-4 py-3 transition hover:bg-indigo-50/40 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-indigo-200 to-indigo-50 rounded-l-lg"
      />

      <DocumentTextIcon
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-2 h-6 w-6 text-indigo-400/20 transition-transform duration-300 group-hover:translate-y-[-1px] group-hover:translate-x-[-1px] group-hover:text-indigo-500/25"
      />

      <div className="relative z-10 flex items-start gap-3">
        <div className="mt-0.5 rounded-md bg-indigo-50 text-indigo-600 p-1.5">
          <DocumentTextIcon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 group-hover:text-indigo-700">
            <span className="truncate">{letter.nome}</span>
          </h3>

          {letter.descrizione ? (
            <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">
              {letter.descrizione}
            </p>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function LetterCard({ letter, canGenerate, onLetterClick }) {
  const categoryName = letter.categoria_info?.nome || "";

  return (
    <Link
      to={`/lettere/${letter.id}`}
      onClick={(event) => onLetterClick(event, canGenerate)}
      className="group relative block min-h-[175px] overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-indigo-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      <DocumentTextIcon
        className="pointer-events-none absolute -right-2 -bottom-2 h-24 w-24 text-indigo-200/20 transition-transform duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:text-indigo-300/30"
        aria-hidden="true"
      />

      <div className="relative z-10 flex h-full flex-col">
        <h3 className="flex items-center gap-2 text-[1.05rem] font-semibold leading-snug text-slate-700 transition-colors group-hover:text-indigo-700">
          <span className="min-w-0">{letter.nome}</span>
        </h3>

        <div className="mt-3 min-h-[28px]">
          {letter.descrizione ? (
            <p className="text-sm text-gray-500 line-clamp-2">
              {letter.descrizione}
            </p>
          ) : (
            <p className="text-sm text-transparent select-none">.</p>
          )}
        </div>

        <div className="mt-auto pt-5">
          <div className="border-t border-gray-200" />

          <div className="mt-4 flex items-center justify-between gap-3">
            <div>
              {categoryName ? (
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                  {categoryName}
                </span>
              ) : null}
            </div>

            <span className="text-sm font-semibold text-indigo-600 transition-transform group-hover:translate-x-0.5">
              Genera →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function matchesCategory(letter, activeCategory) {
  if (activeCategory === ALL_KEY) return true;
  const categoriaId = letter?.categoria_info?.id ?? letter?.categoria;
  return categoriaId === activeCategory;
}

function matchesSearch(letter, query) {
  if (!query) return true;

  const nome = (letter?.nome || "").toLowerCase();
  const descrizione = (letter?.descrizione || "").toLowerCase();

  return nome.includes(query) || descrizione.includes(query);
}

export default function LettersCreate() {
  const { token, profile } = useContext(AuthContext);

  const [letters, setLetters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(ALL_KEY);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("cards");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isProfileComplete = profile?.has_complete_profile;
  const normalizedSearch = search.trim().toLowerCase();  

  const canGenerateLetters =
    profile?.customer_type !== "employee" ||
    profile?.employee?.permissions.includes("hr.manage_custom_letters_ou") ||
    profile?.employee?.permissions.includes("hr.manage_custom_letters_all");


  const handleLetterClick = (event, canGenerate) => {
    if (canGenerate) return;

    event.preventDefault();

    Swal.fire({
      icon: "warning",
      title: "Operazione non consentita",
      text: "Non hai i permessi per eseguire questa azione.",
      confirmButtonText: "OK",
    });
  };
  
  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [lettersRes, categoriesRes] = await Promise.all([
          getLettereList(token, ''),
          getLettereCategories(token, ''),
        ]);

        if (cancelled) return;

        setLetters(Array.isArray(lettersRes) ? lettersRes : []);
        setCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
      } catch (err) {
        if (cancelled) return;
        setError(err);
      } finally {
        if (cancelled) return;
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const filteredLetters = useMemo(() => {
    return (Array.isArray(letters) ? letters : []).filter(
      (letter) =>
        matchesCategory(letter, activeCategory) &&
        matchesSearch(letter, normalizedSearch)
    );
  }, [letters, activeCategory, normalizedSearch]);

  const handleResetFilters = () => {
    setSearch("");
    setActiveCategory(ALL_KEY);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <PopoverComponentCustom title="Generazione Lettere e Contratti" />

      {profile?.customer_type === "company" &&
        !isProfileComplete &&
        profile?.company?.email === profile?.the_real_user_email && (
          <div className="w-full">
            <div className="bg-orange-100 text-orange-600 p-4">
              <p className="text-center text-sm font-semibold">
                Il profilo non è completo. <br />
                Compila i campi mancanti{" "}
                <Link
                  to="/profilo/fatturazione"
                  className="text-indigo-600 hover:text-indigo-800 underline"
                >
                  cliccando qui
                </Link>
              </p>
            </div>
          </div>
        )}

      <main className="flex-grow py-8">
        <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-indigo-50 to-white p-3 ring-1 ring-black/5">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} />
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>

              <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[250px_minmax(0,1fr)]">
            <div>
              <SidebarCategories
                categories={categories}
                activeKey={activeCategory}
                onChange={setActiveCategory}
              />
            </div>

            <div className="min-w-0">
              <section>
                {isLoading ? (
                  <LoadingState
                    spinnerSize={30}
                    label="Caricamento..."
                    center={true}
                    fullWidth={true}
                    minHeight="auto"
                    className="inline-block align-middle border border-dashed rounded-md border-gray-300 py-2 h-48"
                  />
                ) : error ? (
                  <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span>Errore nel caricamento delle lettere</span>
                  </div>
                ) : filteredLetters.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-12">
                    <ExclamationTriangleIcon className="h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      Nessuna lettera trovata con i filtri correnti
                    </p>
                  </div>
                ) : viewMode === "list" ? (
                  <div className="space-y-2">
                    {filteredLetters.map((letter) => (
                      <LetterRow key={letter.id} letter={letter} canGenerate={canGenerateLetters} onLetterClick={handleLetterClick} />
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                    {filteredLetters.map((letter) => (
                      <LetterCard key={letter.id} letter={letter} canGenerate={canGenerateLetters} onLetterClick={handleLetterClick} />
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}